import { Stack, Typography, TextField, Box } from "@mui/material";
import { ChangeEvent, useEffect, useState, type ReactNode } from "react";
import { useTBContext } from "../../Contexts/useTBStates";
import { IOSSwitch } from "../../../shared/Components/iosSwitch";


interface MatchPlayPoints {
  pointsPerHole: number;
  splitOrPush: boolean;
}

const matchPlayPointsTemplate: MatchPlayPoints = {
    pointsPerHole: 1,
    splitOrPush: false
}

const isMatchPlayPoints = (value: unknown): value is MatchPlayPoints => {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;

    const points = (value as { pointsPerHole?: unknown }).pointsPerHole;
    const splitOrPush = (value as { splitOrPush?: unknown }).splitOrPush;

    return typeof points === "number" && typeof splitOrPush === "boolean";
};

const getCurrentHolePoints = (viewEvent: any, roundValue: number): MatchPlayPoints => {
    const maybeHolePoints = viewEvent?.rounds?.[roundValue - 1]?.holePoints;
    return isMatchPlayPoints(maybeHolePoints) ? maybeHolePoints : matchPlayPointsTemplate;
};

interface PointsTextFieldProps {
  factor: number;
  appTheme: any;
  [key: string]: any;
}

const PointsTextField = (props: PointsTextFieldProps) => {
  const { factor, appTheme, ...otherProps } = props;
  return (
    <TextField
      {...otherProps}
      size="small"
      sx={{
        width: 72 * (factor || 1),
        padding: 0,
        "& .MuiInputBase-input": {
          fontSize: 14 * (factor || 1),
          height: 24 * (factor || 1),
          padding: '4px 6px',
          textAlign: 'center',
        },
        '& .MuiOutlinedInput-root': { padding: 0 },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: appTheme.palette.primary.main,
        },
      }}
    />
  );
};

export const MatchPlayPointsLayout = () => {

    const { viewEvent, roundValue, editMode, appTheme, factor, setViewEvent } = useTBContext();

    const [holePoints, setHolePoints] = useState<MatchPlayPoints>(getCurrentHolePoints(viewEvent, roundValue));

    useEffect(() => {
        setHolePoints(getCurrentHolePoints(viewEvent, roundValue));
    }, [viewEvent, roundValue]);

    const updateViewEvent = (nextHolePoints: MatchPlayPoints) => {
        if (!viewEvent?.rounds?.[roundValue - 1]) return;

        const newViewEvent = JSON.parse(JSON.stringify(viewEvent));
        newViewEvent.rounds[roundValue - 1].holePoints = nextHolePoints;
        setViewEvent(JSON.parse(JSON.stringify(newViewEvent)));
    }

    const handlePointsChange = (e: ChangeEvent<HTMLInputElement>) => {
        const newHolePoints = {...holePoints};
        newHolePoints.pointsPerHole = Number(e.target.value);
        setHolePoints(newHolePoints);
        updateViewEvent(newHolePoints);
    }

    const handleTiesChange = (e: ChangeEvent<HTMLInputElement>) => {
        const newHolePoints = {...holePoints};
        newHolePoints.splitOrPush = e.target.checked;
        setHolePoints(newHolePoints);
        updateViewEvent(newHolePoints);
    }

    const fieldRow = (label: string, value: ReactNode) => (
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
        >
            <Typography
                flex={2}
                textAlign="left"
                sx={{
                    fontWeight: 500,
                    fontSize: 14 * factor,
                    lineHeight: 1.4,
                    minHeight: 24 * factor,
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                    flexShrink: 1,
                }}
            >
                {label}
            </Typography>
            <Box sx={{ flex: 3, minWidth: 0, display: 'flex', alignItems: 'center' }}>
                {value}
            </Box>
        </Stack>
    );

    return (
        <Stack direction="column" spacing={1.25} sx={{ width: '100%', minWidth: 0, pt: 0.5 }}>
            {fieldRow(
                'Points Per Hole:',
                <PointsTextField
                    factor={factor}
                    appTheme={appTheme}
                    value={holePoints.pointsPerHole}
                    disabled={!editMode}
                    onChange={handlePointsChange}
                />,
            )}
            {fieldRow(
                'Ties:',
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography sx={{ fontSize: 14 * factor, fontWeight: 500 }}>Split</Typography>
                    <IOSSwitch
                        checked={holePoints.splitOrPush}
                        disabled={!editMode}
                        onChange={handleTiesChange}
                    />
                    <Typography sx={{ fontSize: 14 * factor, fontWeight: 500 }}>Push</Typography>
                </Stack>,
            )}
        </Stack>
    )
}
